import React, { useEffect, useState } from 'react';
import { AuthLink as Link } from '../auth/AuthContext';
import api from '../api';
import Avatar from '../components/Avatar';
import Spinner from '../components/Spinner';
import SocialLoginButtons from '../components/SocialLoginButtons';
import AuthShell from '../auth/AuthShell';
import { useAuthContext } from '../auth/AuthContext';

const LoginPage = () => {
    const authContext = useAuthContext();
    // Состояния: 'loading' | 'chooser' | 'form' | 'twofa'
    const [viewState, setViewState] = useState('loading');
    const [accounts, setAccounts] = useState([]);

    // Данные формы
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);

    const [twoFaCode, setTwoFaCode] = useState('');

    // Идёт вход выбранным аккаунтом. Пока он не завершён, нажимать больше не на что:
    // переключение аккаунта - это запрос к серверу, и второе нажатие начинало его заново
    // поверх незаконченного первого, разрушая поток авторизации.
    const [pendingAccountId, setPendingAccountId] = useState(null);
    // То же для форм: пока запрос в пути, кнопка занята.
    const [submitting, setSubmitting] = useState(false);

    const busy = pendingAccountId !== null || submitting;

    // 1. При загрузке страницы проверяем сессии
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('error') === 'social') setError('Не удалось войти через провайдера. Попробуйте снова.');
        if (params.get('error') === 'social_email_conflict') setError('Войдите в существующий аккаунт и привяжите этого провайдера в разделе безопасности. Совпадения почты недостаточно для автоматической привязки.');
        if (params.get('error') === 'social_link_conflict') setError('Этот профиль провайдера уже привязан к другому аккаунту или текущий аккаунт изменился.');

        api.get('/api/auth/sessions')
            .then(res => {
                if (res.data.length > 0) {
                    setAccounts(res.data);
                    setViewState('chooser');
                } else {
                    setViewState('form');
                }
            })
            .catch(() => {
                // Если ошибка сети - показываем форму как фоллбэк
                setViewState('form');
            });
    }, [authContext.oauth]);

    // 2. Логика переключения аккаунта (Switch)
    const handleAccountClick = (userId) => {
        if (busy) return;

        setError(null);
        setPendingAccountId(userId);

        api.get(`/api/auth/switch/${userId}`)
            .then(res => {
                // Блокировку намеренно не снимаем: дальше уход со страницы, и вернувшиеся
                // в рабочее состояние кнопки в этот момент означали бы приглашение нажать
                // ещё раз посреди перехода.
                window.location.href = res.data.targetUrl;
            })
            .catch(() => {
                setPendingAccountId(null);
                setError('Не удалось войти в этот аккаунт. Попробуйте ещё раз или войдите по паролю.');
            });
    };

    // 3. Логика входа по паролю (Form Login)
    const handleFormLogin = async (e) => {
        e.preventDefault();
        if (busy) return;

        setError(null);
        setSubmitting(true);

        const formData = new URLSearchParams();
        formData.append('username', username);
        formData.append('password', password);

        try {
            const response = await api.post('/api/auth/login', formData, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });
            if (response.data.twoFaRequired) {
                setViewState('twofa');
                setSubmitting(false);
            } else {
                window.location.href = response.data.targetUrl;
            }
        } catch (err) {
            setError('Неверный логин или пароль');
            setSubmitting(false);
        }
    };

    const handleTwoFaSubmit = async (e) => {
        e.preventDefault();
        if (busy) return;

        setError(null);
        setSubmitting(true);
        try {
            const formData = new URLSearchParams();
            formData.append('code', twoFaCode);
            const response = await api.post('/api/auth/2fa/verify', formData, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });
            window.location.href = response.data.targetUrl;
        } catch (err) {
            setError('Неверный 2FA код');
            setSubmitting(false);
        }
    };


    const errorBox = error && <p role="alert" className="auth-error">{error}</p>;
    const socialButtons = <SocialLoginButtons busy={busy} />;
    if (viewState === 'loading') return <AuthShell title="Загрузка"><Spinner /></AuthShell>;
    if (viewState === 'chooser') return <AuthShell title="Выберите аккаунт" footer={<button className="auth-secondary w-full" disabled={busy} onClick={() => setViewState('form')}>Добавить аккаунт</button>}>
        <p className="auth-description">Продолжите с одним из аккаунтов на этом устройстве.</p><div className="auth-accounts">{accounts.map(account => <button key={account.id} className="auth-account" disabled={busy} onClick={() => handleAccountClick(account.id)} aria-busy={pendingAccountId === account.id}><Avatar src={account.avatarUrl} name={account.name} email={account.email} size={44} /><span className="min-w-0 text-left"><strong className="block truncate">{account.name || 'Без имени'}</strong><span className="block truncate text-sm text-gray-500">{account.email}</span></span>{pendingAccountId === account.id && <Spinner size={18} />}</button>)}</div>{errorBox}
    </AuthShell>;
    if (viewState === 'twofa') return <AuthShell title="Подтверждение 2FA"><button className="auth-back" disabled={busy} onClick={() => setViewState('form')}>← Назад</button><p className="auth-description">Введите код из приложения-аутентификатора.</p><form onSubmit={handleTwoFaSubmit} className="flex flex-col gap-5"><input type="text" inputMode="numeric" autoComplete="one-time-code" aria-label="Код подтверждения" placeholder="Код подтверждения" value={twoFaCode} onChange={e => setTwoFaCode(e.target.value)} disabled={busy} required autoFocus />{errorBox}<button className="auth-primary" disabled={busy}>{busy ? 'Проверяем…' : 'Подтвердить'}</button></form></AuthShell>;
    return <AuthShell title="Авторизация" footer={socialButtons}>
        {accounts.length > 0 && <button className="auth-back" disabled={busy} onClick={() => setViewState('chooser')}>← Назад к аккаунтам</button>}
        <form onSubmit={handleFormLogin} className="flex flex-col gap-5"><input type="email" aria-label="Email" placeholder="Email" autoComplete="username" value={username} onChange={e => setUsername(e.target.value)} required disabled={busy} /><input type="password" aria-label="Пароль" placeholder="Пароль" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} required disabled={busy} />{errorBox}<button className="auth-primary mt-2" disabled={busy}>{busy ? 'Входим…' : 'Войти'}</button></form>
        <div className="auth-login-links"><Link to="/forgot-password">Забыли пароль?</Link><p>Нет аккаунта? <Link to="/register">Зарегистрируйтесь.</Link></p></div>
    </AuthShell>;
};
export default LoginPage;
